import {
  useCallback,
  useEffect,
  useState,
  type FunctionComponent,
} from "react";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../components/ui/tabs";

import { SocialTableComponents } from "./social-table.components";
import useUserStore from "../../../store/socials.store";
import SearchIcon from "../../../assets/svgs/common/search.svg";
import { Input } from "../../../components/ui/input";
import { useSearchSocialsAdminHttp } from "../../../hooks/http/admin/socials/search-socials.admin.http";
import { debounce, handleHttpError } from "../../../utils/utils";
import { Plus, XIcon } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import { Link } from "react-router-dom";
import { Button } from "../../../components/ui/button";

const DashboardSocialsAdminPartial: FunctionComponent = () => {
  const { setData } = useUserStore();
  const [search, setSearch] = useState("");
  const [currentTab, setCurrentTab] = useState("applied");
  const {
    execute: searchSocialHttp,
    fetching,
    cancel: cancelSearchSocialHttp,
  } = useSearchSocialsAdminHttp();
  const [typing, setTyping] = useState(false);
  const { toast } = useToast();

  const debouncedSearch = useCallback(
    debounce(async (searchValue: string) => {
      const searchString = searchValue.trim();
      if (!searchString) {
        return;
      }
      const response = await searchSocialHttp({
        body: null,
        queryParams: {
          searchString: searchString,
        },
      });
      if (!response.success) {
        return handleHttpError(response, toast);
      }
      setData(response.data.socials);
      setTyping(false);
    }, 500),
    []
  );

  function handleSearch(e: React.ChangeEvent<HTMLInputElement>) {
    cancelSearchSocialHttp();
    setSearch(e.target.value);
    debouncedSearch(e.target.value);
    setTyping(true);
    setCurrentTab("search");
    if (!e.target.value.trim().length) {
      setCurrentTab("applied");
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setData([]);
  }, [setData, currentTab]);

  return (
    <div className="flex flex-col justify-start items-center bg-background p-8 w-full h-screen text-primary container">
      <div className="flex justify-between items-center px-5 py-6 w-full">
        <p className="font-semibold text-2xl">Solo socials</p>
        <div className="flex items-center gap-4 py-4">
          <div className="relative flex items-center bg-bginput py-2 pl-5 border border-border rounded-10">
            <SearchIcon />
            <Input
              value={search}
              onChange={(e) => {
                handleSearch(e);
              }}
              className="bg-transparent border-none w-96 font-semibold text-base placeholder:text-secondary"
              placeholder="Search by name, instagram ID, and number"
            />

            {search.trim().length > 0 && (
              <XIcon
                onClick={() => {
                  setSearch("");
                  setCurrentTab("applied");
                }}
                className="right-5 absolute cursor-pointer"
              />
            )}
          </div>
          <Link to="/admin/socials/add-new">
            <Button className="flex gap-2 bg-primarybuttonbg px-10 py-7 font-semibold text-base text-black">
              <Plus className="w-4 h-4" /> Add a social
            </Button>
          </Link>
        </div>
      </div>

      <div className="w-full">
        <Tabs
          value={currentTab}
          onValueChange={(value) => {
            setCurrentTab(value);
          }}
          className=""
        >
          <TabsList className="">
            <TabsTrigger className="border-x-0 border-t-0" value="applied">
              Pending approval
            </TabsTrigger>
            <TabsTrigger className="border-x-0 border-t-0" value="approved">
              Approved
            </TabsTrigger>
            <TabsTrigger className="border-x-0 border-t-0" value="rejected">
              Rejected
            </TabsTrigger>
            <TabsTrigger className="border-x-0 border-t-0" value="external">
              External
            </TabsTrigger>
            {search.trim().length ? (
              <TabsTrigger className="border-x-0 border-t-0" value="search">
                Search Result
              </TabsTrigger>
            ) : null}
          </TabsList>
          <TabsContent value="applied" className="w-full">
            <SocialTableComponents applicationStatus="applied" />
          </TabsContent>
          <TabsContent value="approved" className="w-full">
            <SocialTableComponents applicationStatus="approved" />
          </TabsContent>
          <TabsContent value="rejected" className="w-full">
            <SocialTableComponents applicationStatus="rejected" />
          </TabsContent>
          <TabsContent value="external" className="w-full">
            <SocialTableComponents applicationStatus="external" />
          </TabsContent>          
          {search.trim().length ? (
            <TabsContent value="search" className="w-full">
              <SocialTableComponents
                searchFetching={fetching || typing}
                tableType="search"
                applicationStatus="search"
              />
            </TabsContent>
          ) : null}
        </Tabs>
      </div>
    </div>
  );
};

export default DashboardSocialsAdminPartial;
